import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, Wallet, Percent, ShoppingBag, Layers, Sparkles, Upload, Trash2, ShieldCheck, FileSpreadsheet, Download, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { KpiTile, KpiSkeleton } from "@/components/exec/KpiTile";
import { SectionCard, PageHeader, ErrorState, EmptyState, Stat } from "@/components/exec/Primitives";
import { SalesTrendChart, AtvWeekdayChart, HolidayLegend } from "@/components/exec/Charts";
import { StoreTable, PatternSignals, CorrelationScatter, MixDonut, MixWeeks, UploadDropzone, Pill } from "@/components/exec/Widgets";
import { InsightCard, InsightsBody, InsightsHeaderActions, ExecutiveSummaryText, useInsights } from "@/components/exec/Insights";
import { useSalesV2, useInvalidateAnalytics } from "@/lib/queries";
import { useScope } from "@/lib/scope";
import { useApp } from "@/lib/app";
import { api, errorMessage, API_BASE_URL } from "@/lib/api";
import { fmtInt, fmtPct, fmtMYR, fmtMYRShort, fmtDate, fmtDateLong, fmtTimeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

const na = (k, txt) => (k && (k.value === null || k.value === undefined) ? txt : undefined);

/* ------------------------------------------------------------------ admin: upload + manage ledgers */
const UploadPanel = ({ stores, scopes, clusters, defaultSite }) => {
  const invalidate = useInvalidateAnalytics();
  const [site, setSite] = useState(defaultSite || stores[0]?.id || "");
  const [mode, setMode] = useState("merge");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (defaultSite) setSite(defaultSite);
  }, [defaultSite]);
  const byCluster = useMemo(() => {
    const g = {};
    stores.forEach((s) => (g[s.cluster] = g[s.cluster] || []).push(s));
    return g;
  }, [stores]);
  const target = stores.find((s) => s.id === site);

  const onFile = async (file) => {
    if (!site) return toast.error("Pick the store this report belongs to");
    setBusy(true);
    setResult(null);
    try {
      const res = await api.uploadLedger(file, site, mode);
      const led = res?.ledger || {};
      setResult({ filename: file.name, receipts: led.receipts, total: led.summary?.total, dates: led.summary?.dates, merged: led.summary?.merged, store: target?.name });
      toast.success(`POS report loaded for ${target?.name || site}`, { description: `${fmtInt(led.receipts)} receipts on file${mode === "merge" ? " (merged by date)" : ""}` });
      invalidate();
    } catch (e) {
      toast.error(errorMessage(e, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sid) => {
    try {
      await api.deleteLedger(sid);
      toast.success("POS ledger removed");
      invalidate();
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove the ledger"));
    }
  };

  return (
    <SectionCard title="POS upload" subtitle="Admin only. Upload the POS 'Daily Sales Item' report (.xlsx) for one store at a time. Receipts are joined to the same store's footfall by date." testId="sales-upload-card" actions={<Badge variant="outline" className="text-[10px] border-gold/30 bg-gold/12 text-gold gap-1"><ShieldCheck size={11} /> Admin</Badge>}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Store</Label>
            <Select value={site} onValueChange={setSite} disabled={busy}>
              <SelectTrigger className="h-10 bg-card/70" data-testid="pos-upload-store-select"><SelectValue placeholder="Choose a store" /></SelectTrigger>
              <SelectContent className="bg-popover/95 backdrop-blur-xl">
                {(clusters || []).map((c) => (
                  <SelectGroup key={c.id}>
                    <SelectLabel className="text-[11px] uppercase tracking-wider">{c.name}</SelectLabel>
                    {(byCluster[c.id] || []).map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`pos-upload-store-${s.id}`}>
                        <span className="flex items-center gap-2">{s.name}{s.has_pos && <span className="text-[10px] text-success">· POS</span>}</span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {target?.ledger && <div className="text-[11px] text-muted-foreground">On file: {target.ledger.filename} · {fmtInt(target.ledger.receipts)} receipts · {fmtDate(target.ledger.first)} – {fmtDate(target.ledger.last)}</div>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">How to apply</Label>
            <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v)} className="justify-start" data-testid="pos-upload-mode">
              <ToggleGroupItem value="merge" className="h-9 text-xs data-[state=on]:bg-gold/15 data-[state=on]:text-foreground" data-testid="pos-upload-mode-merge">Merge by date</ToggleGroupItem>
              <ToggleGroupItem value="replace" className="h-9 text-xs data-[state=on]:bg-gold/15 data-[state=on]:text-foreground" data-testid="pos-upload-mode-replace">Replace everything</ToggleGroupItem>
            </ToggleGroup>
            <p className="text-[11px] text-muted-foreground leading-relaxed">{mode === "merge" ? "Keeps receipts on dates the new report does not cover - monthly exports accumulate into one ledger." : "Drops the store's existing ledger and keeps only this report."}</p>
          </div>
          <a href={`${API_BASE_URL}/sales/ledger/template`} className="text-xs text-gold hover:underline inline-flex items-center gap-1 focus-gold rounded" data-testid="pos-upload-template-link"><Download size={12} /> CSV template (if your POS cannot export Excel)</a>
        </div>
        <div className="lg:col-span-7 space-y-3">
          <UploadDropzone onFile={onFile} busy={busy} testId="sales-pos-upload-dropzone" />
          {result && (
            <div className="rounded-lg border border-success/30 bg-success/10 px-3 py-2.5 text-xs flex items-start gap-2" data-testid="pos-upload-result">
              <FileSpreadsheet size={14} className="text-success mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{result.filename} → {result.store}</div>
                <div className="text-muted-foreground mt-0.5 font-mono">{fmtInt(result.receipts)} receipts on file{result.total ? ` · ${fmtMYR(result.total)}` : ""}{result.dates?.length ? ` · ${fmtDate(result.dates[0])} – ${fmtDate(result.dates[result.dates.length - 1])}` : ""}{result.merged ? ` · ${result.merged.new_receipts} new, ${result.merged.kept_receipts} kept` : ""}</div>
              </div>
            </div>
          )}
          <div className="rounded-lg border border-border/60 bg-muted/10 p-3">
            <div className="eyebrow mb-2">Ledgers on file</div>
            {stores.filter((s) => s.has_pos).length === 0 ? (
              <div className="text-xs text-muted-foreground" data-testid="pos-ledgers-empty">No POS ledgers uploaded yet.</div>
            ) : (
              <ul className="space-y-1.5">
                {stores.filter((s) => s.has_pos).map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`pos-ledger-${s.id}`}>
                    <span className="min-w-0 truncate"><span className="font-medium">{s.name}</span> <span className="text-muted-foreground font-mono">· {fmtInt(s.ledger?.receipts)} receipts · {fmtDate(s.ledger?.first)} – {fmtDate(s.ledger?.last)} · {fmtTimeAgo(s.ledger?.uploaded_at)}</span></span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-danger shrink-0" aria-label={`Remove ledger for ${s.name}`} data-testid={`pos-ledger-remove-${s.id}`}><Trash2 size={13} /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="pos-ledger-remove-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display">Remove the POS ledger for {s.name}?</AlertDialogTitle>
                          <AlertDialogDescription>All {fmtInt(s.ledger?.receipts)} receipts are deleted from the dashboard (not from your POS). Daily totals are kept as a worksheet so conversion history is not lost.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep it</AlertDialogCancel>
                          <AlertDialogAction className="bg-danger text-background hover:bg-danger/90" onClick={() => remove(s.id)} data-testid="pos-ledger-remove-confirm">Remove ledger</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

/* ------------------------------------------------------------------ page */
export default function SalesPos() {
  const navigate = useNavigate();
  const { params, scope } = useScope();
  const { isAdmin, scopes, clusters } = useApp();
  const q = useSalesV2();
  const d = q.data;
  const ins = useInsights("sales", params, !!d);
  const [showUpload, setShowUpload] = useState(false);
  const stale = q.isFetching && q.isPlaceholderData;

  useEffect(() => {
    document.title = d ? `Zapin | ${d.scope.short} · Sales` : "Zapin | Sales & POS";
  }, [d]);

  const uploadStores = useMemo(() => {
    const list = (scopes || []).filter((s) => s.kind === "site");
    const posById = Object.fromEntries((d?.stores || []).map((s) => [s.id, s]));
    return list.map((s) => ({ id: s.id, name: s.name, cluster: s.cluster, has_pos: !!posById[s.id]?.has_pos, ledger: posById[s.id]?.ledger || null }));
  }, [scopes, d]);
  const defaultSite = d?.scope?.kind === "site" ? d.scope.id : undefined;

  const brands = d?.mix?.brands || [];

  return (
    <div className="space-y-6 md:space-y-8" data-testid="sales-pos-page">
      <PageHeader
        eyebrow={d ? `Sales & POS · ${d.scope.kind === "all" ? "All stores" : d.scope.kind === "cluster" ? "Mall" : "Store"}` : "Sales & POS"}
        title={d ? d.scope.name : "Revenue, conversion and product mix"}
        subtitle={d ? `${d.period.label} · ${d.period.compare_label}. Receipts are matched to the same store's footfall day by day, so conversion and spend per visitor are like-for-like.` : "Loading…"}
        testId="sales-header"
        actions={
          isAdmin ? (
            <Button variant={showUpload ? "default" : "outline"} size="sm" className="h-9 gap-1.5" onClick={() => setShowUpload((v) => !v)} data-testid="sales-toggle-upload">
              <Upload size={14} /> {showUpload ? "Hide upload" : "Upload POS report"}
            </Button>
          ) : (
            <Badge variant="outline" className="text-[11px] border-border/70 text-muted-foreground gap-1"><ShieldCheck size={11} /> Upload is admin-only</Badge>
          )
        }
      />

      {q.isError && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} testId="sales-error" />}

      {isAdmin && (showUpload || (d && d.stores_with_pos.length === 0)) && <UploadPanel stores={uploadStores} scopes={scopes} clusters={clusters} defaultSite={defaultSite} />}

      {!d ? (
        <KpiSkeleton n={4} />
      ) : !d.available ? (
        <EmptyState
          icon={Receipt}
          title={d.stores_with_pos.length ? "No POS receipts in this window" : "No POS data uploaded yet"}
          body={
            d.stores_with_pos.length
              ? `${d.stores.filter((s) => s.has_pos).map((s) => `${s.name} (${fmtDate(s.ledger?.first)} – ${fmtDate(s.ledger?.last)})`).join(", ")} ${d.stores_with_pos.length === 1 ? "has" : "have"} a ledger, but none of its dates fall between ${fmtDate(d.period.start)} and ${fmtDate(d.period.end)}. Widen the period or pick the preset that covers the report.`
              : `${isAdmin ? "Upload the POS 'Daily Sales Item' report above" : "An admin can upload the POS report"} to unlock revenue, conversion, basket size and product-mix analysis joined to footfall.`
          }
          testId="sales-empty"
          action={
            d.stores_with_pos.length ? (
              <Button size="sm" variant="outline" className="h-8" onClick={() => navigate("/sales")} data-testid="sales-empty-hint">Use the period picker above</Button>
            ) : isAdmin && !showUpload ? (
              <Button size="sm" className="h-8 gap-1.5" onClick={() => setShowUpload(true)} data-testid="sales-empty-upload"><Upload size={13} /> Upload POS report</Button>
            ) : null
          }
        />
      ) : (
        <div className={cn("space-y-6 md:space-y-8 transition-opacity duration-200", stale && "opacity-70")}>
          {d.stores_without_pos.length > 0 && d.scope.kind !== "site" && (
            <div className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2 text-[11px] text-muted-foreground flex items-start gap-2" data-testid="sales-coverage-note">
              <Store size={12} className="text-warning mt-0.5 shrink-0" />
              <span>Sales figures cover {d.stores_with_pos.length} of {d.stores.length} stores in scope ({d.stores.filter((s) => s.has_pos).map((s) => s.short).join(", ")}). Footfall KPIs on Count Analytics still cover every store.</span>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="sales-kpi-strip">
            <KpiTile index={0} label="Revenue" kpi={d.kpis.revenue} icon={Wallet} compareLabel={d.period.compare_label} testId="kpi-revenue" />
            <KpiTile index={1} label="Receipts" kpi={d.kpis.transactions} icon={Receipt} testId="kpi-transactions" />
            <KpiTile index={2} label="Conversion" kpi={d.kpis.conversion} icon={Percent} hint="Receipts ÷ visitors on the same days." testId="kpi-conversion" />
            <KpiTile index={3} label="Avg basket" kpi={d.kpis.atv} icon={ShoppingBag} hint="Revenue ÷ receipts." testId="kpi-atv" />
            <KpiTile index={4} label="Spend / visitor" kpi={d.kpis.spv} icon={Wallet} hint="Revenue ÷ visitors - the single best measure of how well traffic converts to money." testId="kpi-spv" />
            <KpiTile index={5} label="Units / receipt" kpi={d.kpis.units_per_tx} icon={ShoppingBag} sub={na(d.kpis.units_per_tx, "no quantity column")} testId="kpi-units" />
            <KpiTile index={6} label="Avg daily revenue" kpi={d.kpis.avg_daily_revenue} icon={Wallet} testId="kpi-avg-daily-revenue" />
            <Stat label="Days with sales" value={`${d.totals.days_with_sales} / ${d.coverage.days_in_period}`} sub={`${fmtInt(d.totals.visitors_covered)} visitors on those days`} className="min-h-[128px] flex flex-col justify-center" testId="sales-days-with-sales" />
          </div>

          <SectionCard title="Revenue against footfall, day by day" subtitle="Bars are receipts in RM (left axis); the line is visitors (right axis). Markers follow the holiday overlay toggle." testId="sales-trend-card">
            <SalesTrendChart rows={d.daily} events={d.calendar.events} height={340} testId="sales-trend-chart" />
            <HolidayLegend className="mt-3" />
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <SectionCard className="lg:col-span-7" title="Does more traffic mean more sales?" subtitle="One point per store-day. A tight upward cloud means revenue follows footfall; a flat cloud means conversion, not traffic, is the lever." testId="sales-correlation-card">
              {d.correlation.points.length >= 3 ? <CorrelationScatter points={d.correlation.points} r={d.correlation.pearson_r} height={300} testId="sales-correlation-scatter" /> : <div className="text-sm text-muted-foreground rounded-lg border border-dashed border-border/70 p-4" data-testid="sales-correlation-empty">Need at least three store-days with both receipts and counts to plot the relationship.</div>}
            </SectionCard>
            <SectionCard className="lg:col-span-5" title="Basket and conversion by weekday" subtitle="Average basket (bars) and conversion (line) for each weekday in the period." testId="sales-weekday-card">
              {d.atv_by_weekday.length ? <AtvWeekdayChart rows={d.atv_by_weekday} height={300} testId="sales-atv-weekday-chart" /> : <div className="text-sm text-muted-foreground rounded-lg border border-dashed border-border/70 p-4">No weekday breakdown yet.</div>}
            </SectionCard>
          </div>

          {d.mix.categories.length > 0 && (
            <SectionCard title="Product mix" subtitle={`Receipt lines classified into ${d.mix.scheme === "mixed" ? "each store's own" : d.mix.scheme} categories. Weekly shares show what is gaining or losing ground.`} testId="sales-mix-card" actions={<Layers size={15} className="text-gold" />}>
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                <div className="xl:col-span-5"><MixDonut categories={d.mix.categories} height={240} testId="sales-mix-donut" /></div>
                <div className="xl:col-span-7">
                  {d.mix.weeks.length > 1 ? <MixWeeks weeks={d.mix.weeks} categories={d.mix.categories} height={260} testId="sales-mix-weeks" /> : <div className="text-sm text-muted-foreground rounded-lg border border-dashed border-border/70 p-4 h-full flex items-center">Week-on-week mix needs at least two weeks of receipts in the period.</div>}
                </div>
              </div>
              {brands.length > 0 && (
                <div className="mt-5">
                  <div className="eyebrow mb-2">Top brands by revenue</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="sales-top-brands">
                    {brands.slice(0, 10).map((b) => (
                      <span key={b.brand} className="text-[11px] rounded-md border border-border/70 bg-muted/20 px-2 py-1 inline-flex items-center gap-1.5">
                        <span className="font-medium">{b.brand}</span>
                        <span className="font-mono text-muted-foreground">{fmtMYRShort(b.value)} · {fmtPct(b.share, 0)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {d.stores.length > 1 && (
            <SectionCard title="Store comparison" subtitle="Revenue, conversion and basket for every store in scope. Stores without a POS ledger are listed so the gap is visible." testId="sales-stores-card">
              <StoreTable stores={d.stores} mode="sales" testId="sales-store-table" />
            </SectionCard>
          )}

          <SectionCard title="Pattern signals" subtitle="Deterministic checks: footfall-revenue link, conversion vs basket drivers, weekday and holiday effects, mix shifts." testId="sales-patterns-card">
            <PatternSignals patterns={d.patterns} limit={6} testId="sales-pattern-signals" />
          </SectionCard>
        </div>
      )}

      {d && (
        <section id="insights" className="card-surface p-4 md:p-5 scroll-mt-24" data-testid="sales-insights-card">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h3 className="font-display text-base md:text-lg leading-tight inline-flex items-center gap-2"><Sparkles size={16} className="text-gold" /> AI sales insights</h3>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">What moved revenue - traffic, conversion or basket - and what to do about it. Only available where POS receipts exist in the window.</p>
            </div>
            <InsightsHeaderActions state={{ ...ins.data, starting: ins.starting }} onGenerate={ins.generate} canGenerate={d.available} />
          </div>
          <InsightsBody
            state={ins.isLoading ? { isLoading: true } : ins.data}
            kind="sales"
            renderReady={(data) => (
              <div className="space-y-4">
                <ExecutiveSummaryText text={data.executive_summary} watchlist={data.watchlist} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="sales-insight-grid">
                  {(data.insights || []).map((i, idx) => <InsightCard key={i.id || idx} insight={i} index={idx} />)}
                </div>
              </div>
            )}
          />
        </section>
      )}

      {!d && !q.isError && <Skeleton className="h-80 w-full rounded-xl" />}
      {d && <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground"><Pill ok={d.available}>POS in window</Pill><Pill ok={d.coverage.footfall_days > 0}>footfall {d.coverage.footfall_days} days</Pill><Pill ok={!!d.correlation.pearson_r}>correlation {d.correlation.pearson_r ?? "n/a"}</Pill></div>}
    </div>
  );
}
